import React, { useState } from 'react';
import Navbar from '../../Components/Navbar';
import { Footer } from '../../Components/Footer';
import { Shield, Lock, UserCheck, X, ChevronLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from '../../../TranslationContext';

interface Article {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  date: string;
  content?: string;
}

export const Articles: React.FC = () => {
  const { t } = useTranslation();
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  const colors = {
    white: '#ffffff',
    roseRed: '#c06a70',
    lightBeige: '#f5f5dc',
    blueGray: '#879bae',
    darkText: '#333333',
    lightText: '#ffffff'
  };

  const articles: Article[] = [
    {
      title: t('article_mfa_title'),
      description: t('article_mfa_description'),
      icon: Shield,
      date: t('article_mfa_date'),
      content: t('article_mfa_content')
    },
    {
      title: t('article_tsuriel_title'),
      description: t('article_tsuriel_description'),
      icon: Lock,
      date: t('article_tsuriel_date'),
      content: t('article_tsuriel_content')
    },
    {
      title: t('article_ux_security_title'),
      description: t('article_ux_security_description'),
      icon: UserCheck,
      date: t('article_ux_security_date'),
      content: t('article_ux_security_content')
    }
  ];

  const openArticle = (article: Article) => {
    setSelectedArticle(article);
    window.scrollTo(0, 0);
    document.body.style.overflow = 'hidden';
  };

  const closeArticle = () => {
    setSelectedArticle(null);
    document.body.style.overflow = 'auto';
  };

  const renderMarkdown = (text: string) => {
    if (!text) return '';
    
    let html = text.replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold mb-6 mt-8">$1</h1>');
    html = html.replace(/^## (.*$)/gm, '<h2 class="text-2xl font-semibold mb-4 mt-6">$1</h2>');
    html = html.replace(/^### (.*$)/gm, '<h3 class="text-xl font-semibold mb-3 mt-5">$1</h3>');
    html = html.replace(/^\- (.*$)/gm, '<li class="ml-6 mb-2">$1</li>');
    html = html.replace(/^(?!\<h|\<li)(.*$)/gm, function(match) {
      if (match.trim() === '') return '<br />';
      return '<p class="mb-4">' + match + '</p>';
    });
    
    return html;
  };

  return (
    <div style={{ backgroundColor: colors.lightBeige }} className="min-h-screen">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-700 mb-12 text-center">
          {t('articles_and_news')}
        </h1>
        
        {!selectedArticle && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg shadow-lg overflow-hidden transition duration-300 transform hover:-translate-y-2 hover:shadow-xl group cursor-pointer"
                onClick={() => openArticle(article)}
              >
                <div 
                  className="h-2 transition-all duration-300 group-hover:h-3"
                  style={{ 
                    backgroundColor: index % 2 === 0 
                      ? colors.roseRed 
                      : colors.blueGray
                  }}
                ></div>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div 
                      className="rounded-full p-3 transition-all duration-300 group-hover:scale-110"
                      style={{ 
                        backgroundColor: index % 2 === 0 
                          ? `${colors.roseRed}15` 
                          : `${colors.blueGray}20`
                      }}
                    >
                      <article.icon 
                        className={`h-6 w-6 transition-all duration-300`} 
                        // className={ 'text-roseRed' : 'text-blueGray'} // Appliquez la couleur via className
                      />
                    </div>
                    <span className="text-sm text-slate-400 ml-auto">
                      {article.date}
                    </span>
                  </div>
                  
                  <h2 className="text-xl font-semibold mb-3 text-slate-700 group-hover:text-slate-900 transition duration-300">
                    {article.title}
                  </h2>
                  <p className="mb-5 text-slate-500 group-hover:text-slate-600 transition duration-300">
                    {article.description}
                  </p>
                  <div className="flex justify-end">
                    <span 
                      className="flex items-center text-sm font-medium transition-all duration-300 group-hover:translate-x-1"
                      style={{ 
                        color: index % 2 === 0 
                          ? colors.roseRed 
                          : colors.blueGray
                      }}
                    >
                      {t('read_article')} <ArrowRight className="h-4 w-4 ml-1 transition-all duration-300 group-hover:ml-2" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {selectedArticle && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-white p-4 animate-fadeIn">
            <div className="max-w-4xl mx-auto relative bg-white rounded-lg shadow-xl p-6">
              <div className="flex justify-between items-center mb-8">
                <button 
                  onClick={closeArticle} 
                  className="flex items-center text-slate-500 hover:text-slate-700 transition duration-300 hover:bg-slate-100 rounded-md px-3 py-2"
                >
                  <ChevronLeft className="h-5 w-5 mr-2" />
                  <span>{t('back_to_articles')}</span>
                </button>
                
                <button 
                  onClick={closeArticle} 
                  className="rounded-full p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition duration-300"
                  aria-label={t('close_article_aria')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="pt-4 pb-12">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center">
                    <div 
                      className="rounded-full p-3 mr-4"
                      style={{ backgroundColor: `${colors.roseRed}15` }}
                    >
                      <selectedArticle.icon 
                        className="h-6 w-6 text-roseRed" // Appliquez la couleur via className
                      />
                    </div>
                    <span className="text-sm text-slate-400">
                      {selectedArticle.date}
                    </span>
                  </div>
                </div>
                
                <div 
                  className="prose prose-slate max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedArticle.content || '') }}
                />
                
                <div className="mt-16 text-center">
                  <button 
                    onClick={closeArticle}
                    className="px-6 py-3 rounded-md text-white transition duration-300 hover:shadow-lg transform hover:scale-105"
                    style={{ backgroundColor: colors.roseRed }}
                  >
                    {t('close_article')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {!selectedArticle && <Footer />}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Articles;
// import React, { useState, useEffect } from 'react';
// import { getDefaultArticles, checkOttentiAdmin } from './Utilss';
// import Navbar from '../../Components/Navbar';
// import { Footer } from '../../Components/Footer';
// import ArticleList from './ArticleList';
// import ArticleDetail from './ArticleDetails';
// import ArticleForm from './ArticleForms';
// import { Article } from './typeArticle';
// import { Plus, Shield, UserCheck } from 'lucide-react';


// const Articles: React.FC = () => {
//   const [isDarkMode] = useState<boolean>(false);
//   const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
//   const [isOttentiAdmin, setIsOttentiAdmin] = useState<boolean>(false);
//   const [articles, setArticles] = useState<Article[]>([]);
//   const [isEditing, setIsEditing] = useState<boolean>(false);
//   const [editedArticle, setEditedArticle] = useState<Article | null>(null);
//   const [showAddForm, setShowAddForm] = useState<boolean>(false);
//   const [newArticle, setNewArticle] = useState<Article>({
//     id: '',
//     title: '',
//     description: '',
//     icon: Shield,
//     date: new Date().toISOString().split('T')[0],
//     contentType: 'markdown',
//     content: ''
//   });

//   useEffect(() => {
//     const storedArticles = localStorage.getItem('ottenti_articles');
//     if (storedArticles) {
//       try {
//         const parsedArticles = JSON.parse(storedArticles);
//         const articlesWithIcons = parsedArticles.map((article: any) => {
//           let icon;
//           switch (article.iconName) {
//             case 'Shield': icon = Shield; break;
//             case 'Lock': icon = Lock; break;
//             case 'UserCheck': icon = UserCheck; break;
//             default: icon = Shield;
//           }
//           return { ...article, icon };
//         });
//         setArticles(articlesWithIcons);
//       } catch (e) {
//         console.error("Erreur lors du chargement des articles:", e);
//         setArticles(getDefaultArticles());
//       }
//     } else {
//       setArticles(getDefaultArticles());
//     }
//     checkOttentiAdmin(setIsOttentiAdmin);
//   }, []);

//   useEffect(() => {
//     if (articles.length > 0) {
//       const articlesForStorage = articles.map(article => {
//         let iconName = 'Shield';
//         // if (article.icon === Lock) iconName = 'Lock';
//         if (article.icon === UserCheck) iconName = 'UserCheck';
//         return { ...article, icon: undefined, iconName };
//       });
//       localStorage.setItem('ottenti_articles', JSON.stringify(articlesForStorage));
//     }
//   }, [articles]);

//   const openArticle = (article: Article) => {
//     setSelectedArticle(article);
//     window.scrollTo(0, 0);
//     document.body.style.overflow = 'hidden';
//   };

//   const closeArticle = () => {
//     setSelectedArticle(null);
//     setIsEditing(false);
//     setEditedArticle(null);
//     document.body.style.overflow = 'auto';
//   };

//   const startEditing = () => {
//     if (selectedArticle) {
//       setEditedArticle({ ...selectedArticle });
//       setIsEditing(true);
//     }
//   };

//   const saveChanges = () => {
//     if (editedArticle) {
//       setArticles(articles.map(article =>
//         article.id === editedArticle.id ? editedArticle : article
//       ));
//       setSelectedArticle(editedArticle);
//       setIsEditing(false);
//     }
//   };

//   const deleteArticle = (id: string) => {
//     setArticles(articles.filter(article => article.id !== id));
//     closeArticle();
//   };

//   const addArticle = () => {
//     const articleId = Date.now().toString();
//     const finalArticle = {
//       ...newArticle,
//       id: articleId
//     };
//     setArticles([finalArticle, ...articles]);
//     setShowAddForm(false);
//     setNewArticle({
//       id: '',
//       title: '',
//       description: '',
//       icon: Shield,
//       date: new Date().toISOString().split('T')[0],
//       contentType: 'markdown',
//       content: ''
//     });
//   };

//   return (
//     <div className={isDarkMode ? 'bg-gray-900 min-h-screen' : 'bg-white min-h-screen'}>
//       <Navbar />
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
//         <div className="flex justify-between items-center mb-8">
//           <h1 className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
//             Articles et Actualités
//           </h1>
//           {isOttentiAdmin && (
//             <button
//               onClick={() => setShowAddForm(true)}
//               className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
//             >
//               <Plus className="h-5 w-5 mr-2" />
//               Nouvel article
//             </button>
//           )}
//         </div>
//         {showAddForm && (
//           <ArticleForm
//             article={newArticle}
//             onSave={addArticle}
//             isDarkMode={isDarkMode}
//           />
//         )}
//         {!selectedArticle && (
//           <ArticleList
//             articles={articles}
//             onArticleClick={openArticle}
//             isDarkMode={isDarkMode}
//           />
//         )}
//         {selectedArticle && (
//           <ArticleDetail
//             article={selectedArticle}
//             isEditing={isEditing}
//             editedArticle={editedArticle}
//             onClose={closeArticle}
//             onSave={saveChanges}
//             onEdit={startEditing}
//             onDelete={deleteArticle}
//             isDarkMode={isDarkMode}
//           />
//         )}
//       </div>
//       <Footer />
//     </div>
//   );
// };

// export default Articles;


//Composant article avec coloration bleu indepe,ndatnt
// import React, { useState } from 'react';
// import Navbar from '../../Components/Navbar';
// import { Footer } from '../../Components/Footer';
// import { Shield, Lock, UserCheck, X } from 'lucide-react';

// interface Article {
//   title: string;
//   description: string;
//   icon: React.ComponentType<{ className?: string }>;
//   date: string;
//   content?: string; // Full content of the article
// }

// export const Articles: React.FC = () => {
//   const [isDarkMode] = useState<boolean>(false);
//   const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

//   const articles: Article[] = [
//     {
//       title: "The Importance of Multi-Factor Authentication in 2025",
//       description: "Learn how multi-factor authentication has become essential for the security of modern businesses.",
//       icon: Shield,
//       date: "January 21, 2025",
//       content: `
//         # The Importance of Multi-Factor Authentication in 2025

//         In an increasingly connected world, data security has become a major concern for businesses. Multi-factor authentication (MFA) has emerged as a crucial solution to protect sensitive access.

//         ## Why is MFA Crucial?

//         Cyberattacks are becoming more sophisticated. A simple password is no longer enough to ensure the security of your data. In 2024, over 80% of data breaches involved compromised credentials.

//         Multi-factor authentication adds extra layers of security by combining:
//         - Something you know (password)
//         - Something you have (smartphone, token)
//         - Something you are (fingerprint, facial recognition)

//         ## Concrete Benefits for Your Business

//         - 99.9% reduction in account compromise risks according to recent studies
//         - Compliance with regulations like GDPR, PCI DSS, and others
//         - Enhanced trust from customers and partners
//         - Protection against phishing and brute-force attacks

//         ## How to Effectively Implement MFA

//         Implementing an MFA solution should be thoughtful so as not to harm the user experience. Our experts recommend a gradual approach with a flexible solution that adapts to different usage contexts.

//         Contact us to find out how our solution can secure your business while maintaining a smooth user experience.
//       `
//     },
//     {
//       title: "Securing Your Data with Tsuriel Authentication",
//       description: "An in-depth analysis of the advanced security features of our authentication solution.",
//       icon: Lock,
//       date: "January 18, 2025",
//       content: `
//         # Securing Your Data with Tsuriel Authentication

//         Our Tsuriel Authentication solution represents the state of the art in authentication security. Here’s a detailed analysis of the features that set it apart.

//         ## Advanced Security Architecture

//         Tsuriel Authentication uses a zero-trust architecture with end-to-end encryption. All data is encrypted in transit and at rest with the latest industry-recognized algorithms.

//         ## Key Features

//         1. **Contextual Authentication**
//            Our system evaluates in real-time the risk level of each login attempt by analyzing multiple factors: location, device, user behavior, etc.

//         2. **Next-Generation Biometrics**
//            Beyond fingerprints, our solution integrates 3D facial recognition, voice recognition, and even behavioral analysis.

//         3. **Centralized Policy Management**
//            An intuitive admin console allows you to define granular access policies tailored to different user groups.

//         ## Case Studies

//         Our clients have reported:
//         - 95% reduction in security incidents related to authentication
//         - 60% decrease in support calls for password resets
//         - 25% increase in user satisfaction due to smoother login processes

//         Discover how Tsuriel Authentication can transform your security strategy by scheduling an appointment with one of our experts.
//       `
//     },
//     {
//       title: "How to Improve User Experience While Strengthening Security",
//       description: "Best practices for maintaining a balance between security and usability.",
//       icon: UserCheck,
//       date: "January 15, 2025",
//       content: `
//         # How to Improve User Experience While Strengthening Security

//         Balancing robust security with a smooth user experience is often seen as an insurmountable challenge. However, modern approaches demonstrate that it is possible to achieve both goals simultaneously.

//         ## The False Dilemma of Security vs. Experience

//         Historically, more security meant more friction for the user. Today, smart technologies allow for dynamic adjustments to security levels based on context, reducing friction in low-risk scenarios.

//         ## Effective Strategies

//         ### 1. Progressive Authentication
//         Start with a simple method, then add factors only when the context demands (sensitive operation, unusual behavior).

//         ### 2. Transparent Biometrics
//         Modern biometric technologies can work in the background without active user intervention.

//         ### 3. Secure Single Sign-On (SSO)
//         Allow users to access multiple applications with a single strong authentication rather than remembering multiple credentials.

//         ## Concrete Results

//         Companies that have adopted these approaches have observed:
//         - 35% increase in user adoption of solutions
//         - 40% decrease in security bypasses by employees
//         - Better perception of security policies within the organization

//         Our team can assist you in implementing these strategies to transform security from a perceived obstacle into a competitive advantage.
//       `
//     }
//   ];

//   // Function to display an article in detail view
//   const openArticle = (article: Article) => {
//     setSelectedArticle(article);
//     // Scroll to the top of the page
//     window.scrollTo(0, 0);
//     // Disable scrolling on the main page
//     document.body.style.overflow = 'hidden';
//   };

//   // Function to close the article
//   const closeArticle = () => {
//     setSelectedArticle(null);
//     // Re-enable scrolling on the main page
//     document.body.style.overflow = 'auto';
//   };

//   // Convert simple markdown to basic HTML
//   const renderMarkdown = (text: string) => {
//     if (!text) return '';
    
//     // Replace headings
//     let html = text.replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold mb-6 mt-8">$1</h1>');
//     html = html.replace(/^## (.*$)/gm, '<h2 class="text-2xl font-semibold mb-4 mt-6">$1</h2>');
//     html = html.replace(/^### (.*$)/gm, '<h3 class="text-xl font-semibold mb-3 mt-5">$1</h3>');
    
//     // Replace bullet lists
//     html = html.replace(/^\- (.*$)/gm, '<li class="ml-6 mb-2">$1</li>');
    
//     // Process paragraphs (empty lines)
//     html = html.replace(/^(?!\<h|\<li)(.*$)/gm, function(match) {
//       if (match.trim() === '') return '<br />';
//       return '<p class="mb-4">' + match + '</p>';
//     });
    
//     return html;
//   };

//   return (
//     <div className={isDarkMode ? 'bg-gray-900 min-h-screen' : 'bg-white min-h-screen'}>
//       <Navbar />
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
//         <h1 className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-8`}>
//           Articles and News
//         </h1>
        
//         {/* Article grid */}
//         {!selectedArticle && (
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//             {articles.map((article, index) => (
//               <div 
//                 key={index} 
//                 className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6 cursor-pointer transition transform hover:scale-105 hover:shadow-xl`}
//                 onClick={() => openArticle(article)}
//               >
//                 <article.icon className={`h-8 w-8 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} mb-4`} />
//                 <h2 className={`text-xl font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
//                   {article.title}
//                 </h2>
//                 <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
//                   {article.description}
//                 </p>
//                 <div className="flex justify-between items-center">
//                   <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
//                     {article.date}
//                   </span>
//                   <span className={`text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
//                     Read more →
//                   </span>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}
        
//         {/* Detailed article view */}
//         {selectedArticle && (
//           <div className={`fixed inset-0 z-50 overflow-y-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'} p-4`}>
//             <div className="max-w-4xl mx-auto relative">
//               <button 
//                 onClick={closeArticle} 
//                 className={`absolute right-0 top-0 m-4 p-2 rounded-full ${isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
//                 aria-label="Close the article"
//               >
//                 <X className="h-6 w-6" />
//               </button>
              
//               <div className="pt-12 pb-24">
//                 {/* Icon and date */}
//                 <div className="flex items-center mb-6">
//                   <selectedArticle.icon className={`h-10 w-10 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} mr-4`} />
//                   <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
//                     {selectedArticle.date}
//                   </span>
//                 </div>
                
//                 {/* Article content */}
//                 <div 
//                   className={isDarkMode ? 'text-gray-300' : 'text-gray-800'}
//                   dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedArticle.content || '') }}
//                 />
                
//                 {/* Close button at the bottom */}
//                 <div className="mt-12 text-center">
//                   <button 
//                     onClick={closeArticle}
//                     className={`px-6 py-3 rounded-md ${isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
//                   >
//                     Close the article
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>
//       {!selectedArticle && <Footer />}
//     </div>
//   );
// };

// export default Articles;